import * as iots from "io-ts";

import { DateFormat, formatDateTime, TimeFormat } from "./format-date-time";
import { convertDateToTimeZoneAgnostic, makeDateFromString } from "./Support";
import { isRight } from "fp-ts/lib/Either";

export const glideDateTimeDocumentDataCodec = iots.intersection([
    iots.type({
        kind: iots.literal("glide-date-time"),
        value: iots.number,
    }),
    iots.partial({
        repr: iots.string,
    }),
]);

export type GlideDateTimeDocumentData = iots.TypeOf<typeof glideDateTimeDocumentDataCodec>;

export function isGlideDateTimeDocumentData(x: unknown): x is GlideDateTimeDocumentData {
    return isRight(glideDateTimeDocumentDataCodec.decode(x));
}

export class GlideDateTime {
    private constructor(private readonly _docData: GlideDateTimeDocumentData) {}

    public static fromDocumentData(d: GlideDateTimeDocumentData): GlideDateTime {
        return new GlideDateTime(d);
    }

    public static fromDate(d: Date): GlideDateTime {
        return new GlideDateTime({
            kind: "glide-date-time",
            value: d.getTime(),
        });
    }

    public asDate(): Date {
        return new Date(this._docData.value);
    }

    public asString(): string {
        const { repr } = this._docData;
        if (repr !== undefined) return repr;
        return formatDateTime(this.asDate(), DateFormat.Short, TimeFormat.WithSeconds) ?? "";
    }

    public clone(): GlideDateTime {
        return GlideDateTime.fromDocumentData({ ...this._docData });
    }

    public toDocumentData(): GlideDateTimeDocumentData {
        return { ...this._docData };
    }

    public compareTo(other: unknown): number {
        const { value: leftValue, repr } = this._docData;

        if (other instanceof Date) {
            return this.compareTo(GlideDateTime.fromDate(other));
        } else if (typeof other === "string") {
            // This is all very nasty but our users love to mix strings and dates.
            if (repr !== undefined) {
                // Special case for string comparison when repr? is available;
                // we only will end up dropping repr? when a displayFormula is
                // set on the appropriate column.
                if (repr === other) return 0;

                // If they aren't equal then we'll try parsing the date.
                const otherAsDate = makeDateFromString(other, true);

                if (otherAsDate === undefined) {
                    // This follows the convention of Array.sort
                    return repr < other ? -1 : 1;
                }

                return this.compareTo(convertDateToTimeZoneAgnostic(otherAsDate));
            } else {
                const otherAsDate = makeDateFromString(other, true);
                return otherAsDate === undefined ? 1 : this.compareTo(convertDateToTimeZoneAgnostic(otherAsDate));
            }
        } else if (!(other instanceof GlideDateTime)) {
            if (leftValue === other) return 0;
            // This follows the convention of Array.sort
            return leftValue < (other as any) ? -1 : 1;
        }
        const { value: rightValue } = other._docData;
        return leftValue - rightValue;
    }
}

export function convertGlideDateTime<T>(d: T): T | GlideDateTime {
    if (!isGlideDateTimeDocumentData(d)) return d;
    return GlideDateTime.fromDocumentData(d);
}
